import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, AuthShell, Button, Link, Stack, TextField, Typography, } from '@platform/design-system';
import { useSession } from "../auth/session.js";
import { describeError } from "../http/errors.js";
import { RouterLink } from "../router/router-link.js";
import { emailSchema, passwordSchema, PASSWORD_HINT, PASSWORDS_MATCH } from "./password-rules.js";
import { NewPasswordStep } from "./auth-steps.js";
import { authShellFrame } from "./sign-in-screen.js";
const emailForm = z.object({ email: emailSchema });
/**
 * `/forgot-password`: Cognito emails a code (`ForgotPassword`); the page then moves to the
 * reset step on the same screen. Whether the address exists is never revealed — the message
 * is the same either way (the pool has `preventUserExistenceErrors`).
 */
export function ForgotPasswordScreen({ frame, signInHref = '/login', initialEmail = '', }) {
    const session = useSession();
    const [sent, setSent] = useState(null);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const form = useForm({
        resolver: zodResolver(emailForm),
        defaultValues: { email: initialEmail },
    });
    const request = form.handleSubmit(async ({ email }) => {
        setBusy(true);
        setError(null);
        try {
            const { destination } = await session.forgotPassword(email);
            setSent({ email, destination });
        }
        catch (e) {
            setError(describeError(e, 'Could not send the code. Try again.'));
        }
        finally {
            setBusy(false);
        }
    });
    if (done) {
        return (_jsx(AuthShell, { ...authShellFrame(frame), title: "Password changed", lead: "Sign in with your new password.", children: _jsx(Button, { component: RouterLink, href: signInHref, variant: "contained", size: "large", children: "Go to sign in" }) }));
    }
    if (sent) {
        return (_jsx(AuthShell, { ...authShellFrame(frame), title: "Check your email", lead: `If an account exists for ${sent.email}, a code is on its way${sent.destination ? ` to ${sent.destination}` : ''}.`, children: _jsx(ResetStep, { email: sent.email, onDone: () => setDone(true), onResend: () => {
                    setSent(null);
                } }) }));
    }
    return (_jsx(AuthShell, { ...authShellFrame(frame), title: "Forgot your password?", lead: "Enter your email and we will send a code to choose a new one.", children: _jsx("form", { onSubmit: (e) => void request(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [error && _jsx(Alert, { severity: "error", children: error }), _jsx(TextField, { label: "Email", type: "email", autoComplete: "username", ...form.register('email'), error: form.formState.errors.email?.message }), _jsx(Button, { type: "submit", variant: "contained", size: "large", busy: busy, busyLabel: "Sending\u2026", children: "Send code" }), _jsx(Link, { component: RouterLink, href: signInHref, variant: "body2", sx: { alignSelf: 'flex-start' }, children: "Back to sign in" })] }) }) }));
}
const resetForm = z
    .object({
    code: z.string().trim().min(4, 'Enter the code from the email'),
    password: passwordSchema,
    confirm: z.string(),
})
    .refine(...PASSWORDS_MATCH);
/** Code + new password (`ConfirmForgotPassword`). */
export function ResetStep({ email, onDone, onResend, }) {
    const session = useSession();
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const form = useForm({
        resolver: zodResolver(resetForm),
        defaultValues: { code: '', password: '', confirm: '' },
    });
    const submit = form.handleSubmit(async (v) => {
        setBusy(true);
        setError(null);
        try {
            await session.resetPassword({ email, code: v.code, newPassword: v.password });
            onDone();
        }
        catch (e) {
            setError(describeError(e, 'Could not change the password. Try again.'));
        }
        finally {
            setBusy(false);
        }
    });
    return (_jsx("form", { onSubmit: (e) => void submit(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [error && _jsx(Alert, { severity: "error", children: error }), _jsx(TextField, { label: "Code from the email", autoComplete: "one-time-code", inputMode: "numeric", ...form.register('code'), error: form.formState.errors.code?.message }), _jsx(TextField, { label: "New password", type: "password", autoComplete: "new-password", ...form.register('password'), error: form.formState.errors.password?.message, hint: PASSWORD_HINT }), _jsx(TextField, { label: "Confirm new password", type: "password", autoComplete: "new-password", ...form.register('confirm'), error: form.formState.errors.confirm?.message }), _jsx(Button, { type: "submit", variant: "contained", size: "large", busy: busy, busyLabel: "Saving\u2026", children: "Change password" }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["No email?", ' ', _jsx(Link, { component: "button", type: "button", onClick: onResend, variant: "body2", children: "Send a new code" })] })] }) }));
}
const changeForm = z
    .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    password: passwordSchema,
    confirm: z.string(),
})
    .refine(...PASSWORDS_MATCH);
/** Signed in: `ChangePassword` with the current one (account settings). Answers with `onDone`. */
export function ChangePasswordForm({ onDone, onCancel, }) {
    const session = useSession();
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const form = useForm({
        resolver: zodResolver(changeForm),
        defaultValues: { currentPassword: '', password: '', confirm: '' },
    });
    const submit = form.handleSubmit(async (v) => {
        setBusy(true);
        setError(null);
        try {
            await session.changePassword({ currentPassword: v.currentPassword, newPassword: v.password });
            form.reset();
            onDone();
        }
        catch (e) {
            setError(describeError(e, 'Could not change the password. Try again.'));
        }
        finally {
            setBusy(false);
        }
    });
    return (_jsx("form", { onSubmit: (e) => void submit(e), noValidate: true, children: _jsxs(Stack, { spacing: 2, children: [error && _jsx(Alert, { severity: "error", children: error }), _jsx(TextField, { label: "Current password", type: "password", autoComplete: "current-password", ...form.register('currentPassword'), error: form.formState.errors.currentPassword?.message }), _jsx(TextField, { label: "New password", type: "password", autoComplete: "new-password", ...form.register('password'), error: form.formState.errors.password?.message, hint: PASSWORD_HINT }), _jsx(TextField, { label: "Confirm new password", type: "password", autoComplete: "new-password", ...form.register('confirm'), error: form.formState.errors.confirm?.message }), _jsxs(Stack, { direction: "row", spacing: 1, sx: { justifyContent: 'flex-end' }, children: [onCancel && (_jsx(Button, { onClick: onCancel, disabled: busy, children: "Cancel" })), _jsx(Button, { type: "submit", variant: "contained", busy: busy, busyLabel: "Saving\u2026", children: "Change password" })] })] }) }));
}
export { NewPasswordStep };
//# sourceMappingURL=password-screens.js.map